import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Text } from '@datanomik/core-ui';
import {
  BankLine,
  FileList3Line,
  FileTextLine,
  LineChartLine,
  PieChart2Line,
  PriceTag3Line,
  SafeLine,
  SearchEyeLine,
  ShieldKeyholeLine,
  StockLine,
  WalletLine,
} from '@datanomik/core-ui/icons';

const HIGHLIGHTS = [
  { titleKey: 'login_feature_cash_position_title', descKey: 'login_feature_cash_position_desc', Icon: WalletLine },
  { titleKey: 'login_feature_debts_title', descKey: 'login_feature_debts_desc', Icon: SafeLine },
  { titleKey: 'login_feature_tariffs_title', descKey: 'login_feature_tariffs_desc', Icon: PriceTag3Line },
  { titleKey: 'login_feature_search_title', descKey: 'login_feature_search_desc', Icon: SearchEyeLine },
  { titleKey: 'login_feature_statements_title', descKey: 'login_feature_statements_desc', Icon: FileTextLine },
  { titleKey: 'login_feature_receipts_title', descKey: 'login_feature_receipts_desc', Icon: FileList3Line },
  { titleKey: 'login_feature_reports_title', descKey: 'login_feature_reports_desc', Icon: PieChart2Line },
  { titleKey: 'login_feature_cash_flow_title', descKey: 'login_feature_cash_flow_desc', Icon: LineChartLine },
  { titleKey: 'login_feature_multibank_title', descKey: 'login_feature_multibank_desc', Icon: BankLine },
  { titleKey: 'login_feature_investments_title', descKey: 'login_feature_investments_desc', Icon: StockLine },
  { titleKey: 'login_feature_security_title', descKey: 'login_feature_security_desc', Icon: ShieldKeyholeLine },
];

type Highlight = (typeof HIGHLIGHTS)[number];

const VISIBLE = 4;
const ROTATE_MS = 4200;
const SWAP_MS = 520;

function HighlightContent({ highlight, animation }: { highlight: Highlight; animation: string }) {
  const { t } = useTranslation();
  const { titleKey, descKey, Icon } = highlight;

  return (
    <div className={`absolute inset-0 flex flex-col justify-between gap-2 p-4 ${animation}`}>
      <span className="grid size-8 shrink-0 place-items-center rounded-[9px] bg-white/10">
        <Icon aria-hidden="true" className="size-4 text-brand-foreground/75" />
      </span>

      <div>
        <Text variant="body-sm" weight="semibold" className="text-brand-foreground/90">
          {t(titleKey)}
        </Text>
        <Text variant="caption" className="mt-0.5 line-clamp-2 text-brand-foreground/55">
          {t(descKey)}
        </Text>
      </div>
    </div>
  );
}

export function AuthHighlights() {
  const slotsRef = useRef(HIGHLIGHTS.slice(0, VISIBLE).map((_, index) => index));
  const [slots, setSlots] = useState(slotsRef.current);
  const [leaving, setLeaving] = useState<Record<number, number>>({});
  const nextHighlightRef = useRef(VISIBLE % HIGHLIGHTS.length);
  const nextSlotRef = useRef(0);

  useEffect(() => {
    let swapTimeout: ReturnType<typeof setTimeout>;

    const timer = setInterval(() => {
      const slot = nextSlotRef.current;
      const incoming = nextHighlightRef.current;

      setLeaving((prev) => ({ ...prev, [slot]: slotsRef.current[slot]! }));

      slotsRef.current = slotsRef.current.map((value, index) => (index === slot ? incoming : value));
      setSlots(slotsRef.current);

      nextHighlightRef.current = (incoming + 1) % HIGHLIGHTS.length;
      nextSlotRef.current = (slot + 1) % VISIBLE;

      swapTimeout = setTimeout(() => {
        setLeaving((prev) => {
          const next = { ...prev };
          delete next[slot];
          return next;
        });
      }, SWAP_MS);
    }, ROTATE_MS);

    return () => {
      clearInterval(timer);
      clearTimeout(swapTimeout);
    };
  }, []);

  return (
    <div role="list" className="grid max-w-xl grid-cols-2 auto-rows-[116px] gap-3">
      {slots.map((highlightIndex, slot) => {
        const outgoing = leaving[slot];
        return (
          <div
            key={`highlight-slot-${slot}`}
            role="listitem"
            className="relative h-full overflow-hidden rounded-xl border border-white/10 bg-white/[0.07]"
          >
            {outgoing !== undefined && (
              <HighlightContent
                key={`out-${outgoing}`}
                highlight={HIGHLIGHTS[outgoing]!}
                animation="animate-swap-out"
              />
            )}
            <HighlightContent
              key={`in-${highlightIndex}`}
              highlight={HIGHLIGHTS[highlightIndex]!}
              animation="animate-swap-in"
            />
          </div>
        );
      })}
    </div>
  );
}
